/**
 * Date utilities for handling timezone-specific operations
 * All dates in the system use America/Sao_Paulo timezone
 */

export const TIMEZONE = "America/Sao_Paulo";

/**
 * Parse a time string (HH:MM) into hour and minute
 */
export const parseTime = (timeString: string): { hour: number; minute: number } => {
  if (!timeString) return { hour: 0, minute: 0 };

  // Extract time from HH:MM or HH:MM:SS format
  const timeMatch = timeString.match(/^(\d{2}):(\d{2})/);
  if (timeMatch) {
    return {
      hour: parseInt(timeMatch[1], 10),
      minute: parseInt(timeMatch[2], 10),
    };
  }

  return { hour: 0, minute: 0 };
};

/**
 * Format a time string (HH:MM) for display
 */
export const formatTime = (timeString: string): string => {
  if (!timeString) return "";

  // If it's already in HH:MM format, return as is
  if (timeString.match(/^\d{2}:\d{2}$/) || timeString.match(/^\d{2}:\d{2}:\d{2}$/)) {
    return timeString.slice(0, 5);
  }

  // If it's a full datetime string, extract just the time
  try {
    const date = new Date(timeString);
    return date.toLocaleTimeString("pt-BR", {
      timeZone: TIMEZONE,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return timeString;
  }
};

export const formatDateForDisplay = (dateString: string): string => {
  // Handle YYYY-MM-DD format by adding time to avoid timezone issues
  const date = new Date(dateString + "T12:00:00");
  return date.toLocaleDateString("pt-BR", {
    timeZone: TIMEZONE,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export const formatDateShort = (dateString: string): string => {
  // Handle YYYY-MM-DD format by adding time to avoid timezone issues
  const date = new Date(dateString + "T12:00:00");
  return date.toLocaleDateString("pt-BR", {
    timeZone: TIMEZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

/**
 * Get today's date in YYYY-MM-DD format using Brazil timezone
 */
export const getTodayInBrazil = (): string => {
  return createDateInBrazil(new Date().toISOString().split('T')[0]).toISOString().split('T')[0];
};

/**
 * Format time for display using Brazil timezone
 */
export const formatTimeForDisplay = (timeString: string): string => {
  return timeString; // Time slots are already in local format (HH:MM)
};

/**
 * Get current time in Brazil timezone
 */
export const getCurrentTimeInBrazil = (): Date => {
  const now = new Date();
  return createDateInBrazil(now.toISOString().split('T')[0]);
};

/**
 * Check if a date is valid and not in the past (Brazil timezone)
 */
export const isValidFutureDate = (dateString: string): boolean => {
  const inputDate = createDateInBrazil(dateString);
  const today = getTodayInBrazil();
  const todayDate = createDateInBrazil(today);

  return inputDate.getTime() >= todayDate.getTime();
};

/**
 * Convert a date to Brazil timezone and format for API
 */
export const formatDateForAPI = (dateString: string): string => {
  return dateString; // Keep ISO format for API
};

/**
 * Get localized date/time string for Brazil
 */
export const getLocalizedDateTime = (date: Date): string => {
  return date.toLocaleString("pt-BR", {
    timeZone: TIMEZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Format a date/time string for display using Brazil locale.
 * Accepts both date-only (YYYY-MM-DD) and full ISO datetime (e.g. 2026-03-06T20:39:33.000Z).
 */
export const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return "-";
  const isFullISO = /^\d{4}-\d{2}-\d{2}T\d/.test(dateString.trim());
  const date = isFullISO ? new Date(dateString) : createDateInBrazil(dateString);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("pt-BR", {
    timeZone: TIMEZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

/**
 * Check if a birthday (date string) matches today's date (month and day)
 * @param birthday - Date string in YYYY-MM-DD format
 * @returns true if the birthday month and day match today's month and day
 */
export const isBirthdayToday = (birthday?: string): boolean => {
  if (!birthday) return false;
  const today = createDateInBrazil(getTodayInBrazil());
  const birthdayDate = createDateInBrazil(birthday);
  return (
    today.getMonth() === birthdayDate.getMonth() &&
    today.getDate() === birthdayDate.getDate()
  );
};


/**
 * Create a Date object for a given date string in Brazil timezone
 * @param dateString - Date in YYYY-MM-DD format
 * @returns Date object representing the date at midnight in Brazil timezone
 */
export const createDateInBrazil = (dateString: string): Date => {
  const brazilDate = new Date(`${dateString}T12:00:00`).toLocaleDateString('en-CA', {
    timeZone: TIMEZONE
  });

  const date = new Date(`${brazilDate}T00:00:00`);
  return date;
};

/**
 * Apply DD/MM/YYYY mask to birthday input
 * @param value - Raw input value
 * @returns Formatted string in DD/MM/YYYY format
 */
export const applyBirthdayMask = (value: string): string => {
  // Remove all non-numeric characters
  const numbers = value.replace(/\D/g, "");

  // Apply mask based on length
  if (numbers.length <= 2) {
    return numbers;
  } else if (numbers.length <= 4) {
    return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
  } else {
    return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
  }
};

/**
 * Validate birthday format and value
 * @param value - Date string in DD/MM/YYYY format
 * @returns Validation result with isValid flag and optional error message
 */
export const validateBirthday = (value: string): { isValid: boolean; error?: string } => {
  if (!value.trim()) {
    return { isValid: false, error: "Data de nascimento é obrigatória" };
  }

  // Check format DD/MM/YYYY
  const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const match = value.match(dateRegex);

  if (!match) {
    return { isValid: false, error: "Formato inválido. Use DD/MM/AAAA" };
  }

  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);

  // Validate day
  if (day < 1 || day > 31) {
    return { isValid: false, error: "Dia inválido" };
  }

  // Validate month
  if (month < 1 || month > 12) {
    return { isValid: false, error: "Mês inválido" };
  }

  // Validate year (reasonable range)
  const currentYear = new Date().getFullYear();
  if (year < 1900 || year > currentYear) {
    return { isValid: false, error: "Ano inválido" };
  }

  // Check if date is valid (e.g., not 31/02)
  const date = new Date(year, month - 1, day);
  if (
    date.getDate() !== day ||
    date.getMonth() !== month - 1 ||
    date.getFullYear() !== year
  ) {
    return { isValid: false, error: "Data inválida" };
  }

  // Check if date is not in the future
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (date > today) {
    return { isValid: false, error: "Data de nascimento não pode ser no futuro" };
  }

  return { isValid: true };
};

/**
 * Convert DD/MM/YYYY format to YYYY-MM-DD format for API
 * @param value - Date string in DD/MM/YYYY format
 * @returns Date string in YYYY-MM-DD format
 */
export const convertBirthdayToAPIFormat = (value: string): string => {
  const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const match = value.match(dateRegex);
  if (match) {
    const day = match[1];
    const month = match[2];
    const year = match[3];
    return `${year}-${month}-${day}`;
  }
  return value;
};

/** Month (1–12) and year from today's date in Brazil (YYYY-MM-DD from `getTodayInBrazil`). */
export const getTodayMonthYearBrazil = (): { month: number; year: number } => {
  const [y, m] = getTodayInBrazil().split("-");
  return { year: parseInt(y, 10), month: parseInt(m, 10) };
};

const isLeapYearGregorian = (y: number): boolean =>
  (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;

/**
 * Birthday display: weekday + day/month, no year (America/São_Paulo).
 * Uses the occurrence in the current Brazil calendar year (29 Feb → 28 Feb in non-leap years).
 */
export const formatBirthdayWeekdayDayMonth = (birthdayIso: string): string => {
  const datePart = birthdayIso.split("T")[0];
  const seg = datePart.split("-");
  if (seg.length < 3) return datePart;
  const m = parseInt(seg[1], 10);
  const d = parseInt(seg[2], 10);
  if (Number.isNaN(m) || Number.isNaN(d)) return datePart;

  const { year: yCurrent } = getTodayMonthYearBrazil();
  let day = d;
  if (m === 2 && d === 29 && !isLeapYearGregorian(yCurrent)) {
    day = 28;
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  const candidate = `${yCurrent}-${pad(m)}-${pad(day)}`;
  const inst = new Date(`${candidate}T12:00:00`);
  if (Number.isNaN(inst.getTime())) return datePart;

  const formatted = inst.toLocaleDateString("pt-BR", {
    timeZone: TIMEZONE,
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

/** Parse YYYY-MM-DD (or ISO) into calendar year and month (1–12). */
export const parseYearMonthFromISODate = (date: string): { year: number; month: number } => {
  const ymd = date.split("T")[0];
  const [y, m] = ymd.split("-").map(Number);
  return { year: y, month: m };
};

/** First and last calendar day of a month (month 1–12) as YYYY-MM-DD. */
export const getBrazilCalendarMonthBounds = (
  year: number,
  month: number
): { startDate: string; endDate: string } => {
  const lastDay = new Date(year, month, 0).getDate();
  const ms = String(month).padStart(2, "0");
  return {
    startDate: `${year}-${ms}-01`,
    endDate: `${year}-${ms}-${String(lastDay).padStart(2, "0")}`,
  };
};

/** Add signed calendar days to YYYY-MM-DD (Brazil calendar day arithmetic). */
export const addDaysToYmd = (ymd: string, deltaDays: number): string => {
  const [y, m, d] = ymd.split("-").map((x) => parseInt(x, 10));
  const utc = Date.UTC(y, m - 1, d + deltaDays);
  const dt = new Date(utc);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

/** Inclusive list of YYYY-MM-DD dates from start to end. */
export const eachYmdInclusive = (start: string, end: string): string[] => {
  if (!start) return [];
  if (!end || end < start) return [start];
  const dates: string[] = [];
  let cursor = start;
  let guard = 0;
  while (cursor <= end && guard < 366) {
    dates.push(cursor);
    cursor = addDaysToYmd(cursor, 1);
    guard += 1;
  }
  return dates;
};

/** Weekday + short date, e.g. "segunda-feira, 29/12". */
export const formatWeekdayShortDate = (dateString: string): string => {
  const date = new Date(`${dateString}T12:00:00`);
  return date.toLocaleDateString("pt-BR", {
    timeZone: TIMEZONE,
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
  });
};

/** Monday–Sunday calendar week in Brazil that contains the given YYYY-MM-DD date. */
export const getBrazilCalendarWeekBoundsForDate = (
  date: string
): { startDate: string; endDate: string } => {
  const ref = new Date(`${date}T12:00:00`);
  const weekdayShort = new Intl.DateTimeFormat("en-US", {
    timeZone: TIMEZONE,
    weekday: "short",
  }).format(ref);
  const mon0: Record<string, number> = {
    Mon: 0,
    Tue: 1,
    Wed: 2,
    Thu: 3,
    Fri: 4,
    Sat: 5,
    Sun: 6,
  };
  const daysFromMonday = mon0[weekdayShort] ?? 0;
  const startDate = addDaysToYmd(date, -daysFromMonday);
  const endDate = addDaysToYmd(startDate, 6);
  return { startDate, endDate };
};

/** Monday–Sunday calendar week in Brazil that contains `getTodayInBrazil()`. */
export const getBrazilCalendarWeekBounds = (): {
  startDate: string;
  endDate: string;
} => getBrazilCalendarWeekBoundsForDate(getTodayInBrazil());

/** Same calendar day (today in Brazil) for start/end of day range APIs. */
export const getBrazilTodayBounds = (): { startDate: string; endDate: string } => {
  const t = getTodayInBrazil();
  return { startDate: t, endDate: t };
};

/** All YYYY-MM-DD dates in a calendar month (month 1–12). */
export const getDatesInCalendarMonth = (year: number, month: number): string[] => {
  const lastDay = new Date(year, month, 0).getDate();
  return Array.from({ length: lastDay }, (_, i) => {
    const d = i + 1;
    return `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  });
};

/** Long month label in pt-BR, e.g. "abril de 2026". */
export const formatMonthYearLong = (month: number, year: number): string => {
  const dateString = `${year}-${String(month).padStart(2, "0")}-15`;
  const date = new Date(dateString + "T12:00:00");
  const label = date.toLocaleDateString("pt-BR", {
    timeZone: TIMEZONE,
    month: "long",
    year: "numeric",
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

/** Sunday–Saturday calendar week in Brazil that contains the given YYYY-MM-DD date. */
export const getBrazilSundayWeekBoundsForDate = (
  date: string
): { startDate: string; endDate: string } => {
  const ref = new Date(`${date}T12:00:00`);
  const weekdayShort = new Intl.DateTimeFormat("en-US", {
    timeZone: TIMEZONE,
    weekday: "short",
  }).format(ref);
  const sun0: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  const daysFromSunday = sun0[weekdayShort] ?? 0;
  const startDate = addDaysToYmd(date, -daysFromSunday);
  const endDate = addDaysToYmd(startDate, 6);
  return { startDate, endDate };
};

/** Sunday-start calendar weeks covering a month (includes padding days). */
export const getCalendarMonthWeeks = (
  year: number,
  month: number
): string[][] => {
  const { startDate, endDate } = getBrazilCalendarMonthBounds(year, month);
  const weekStart = getBrazilSundayWeekBoundsForDate(startDate).startDate;
  const weekEnd = getBrazilSundayWeekBoundsForDate(endDate).endDate;

  const weeks: string[][] = [];
  let cursor = weekStart;
  while (cursor <= weekEnd) {
    const week: string[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(addDaysToYmd(cursor, i));
    }
    weeks.push(week);
    cursor = addDaysToYmd(cursor, 7);
  }
  return weeks;
};
